import { reactive } from 'vue';
import { request } from './catalog';
import { locale } from './i18n';
import { logger, storage, storageHealth } from './platform';

/**
 * "What's new": the notes the admin writes. After an update the app shows the notes it has not shown
 * yet once (those marked for a window); the whole history is in the menu. A new installation shows
 * nothing: there is nothing "new" to someone who has just come. Without a server or offline the list
 * is simply empty.
 */
export interface ChangelogEntry {
  id: string;
  version: string;
  title: { ru?: string; en?: string };
  body: { ru?: string; en?: string };
  popup: boolean;
  publishedAt: string;
}

const SEEN = 'changelog.seen';

export const changelog = reactive({
  entries: [] as ChangelogEntry[],
  /** The window is open: the unseen notes ('new') or the whole list ('all'). */
  open: null as null | 'new' | 'all',
  fresh: [] as ChangelogEntry[],
});

export const textOf = (v: { ru?: string; en?: string }): string => (locale.value === 'ru' ? v.ru || v.en : v.en || v.ru) ?? '';

const stamp = (e: ChangelogEntry) => Date.parse(e.publishedAt);

export async function loadChangelog(): Promise<void> {
  try {
    const { res } = await request('/api/v1/changelog');
    if (!res.ok) return;
    const list = ((await res.json()) as { entries: ChangelogEntry[] }).entries.filter((e) => e.publishedAt && Number.isFinite(stamp(e)));
    changelog.entries = list.sort((a, b) => stamp(b) - stamp(a));
  } catch {
    /* offline or no server */
  }
}

/** After the list is loaded: open the window if there are notes this browser has not seen. */
export async function maybeShowChangelog(): Promise<void> {
  await loadChangelog();
  if (!changelog.entries.length || storageHealth.state === 'memory') return;
  try {
    const latest = stamp(changelog.entries[0]);
    const seen = await storage.getSetting<number>(SEEN);
    if (seen === undefined) {
      // first start of this browser: everything so far is old news
      await storage.setSetting(SEEN, latest);
      return;
    }
    const unseen = changelog.entries.filter((e) => stamp(e) > seen);
    if (!unseen.length) return;
    const popups = unseen.filter((e) => e.popup).slice(0, 5);
    if (!popups.length) {
      await storage.setSetting(SEEN, latest);
      return;
    }
    changelog.fresh = popups;
    changelog.open = 'new';
  } catch (e) {
    logger.warn('changelog.failed', { error: e instanceof Error ? e.name : 'unknown' });
  }
}

export function showChangelog(): void {
  changelog.open = 'all';
}

/** The window is closed: what was shown is seen. */
export async function closeChangelog(): Promise<void> {
  const was = changelog.open;
  changelog.open = null;
  if (was === 'new' && changelog.entries.length) await storage.setSetting(SEEN, stamp(changelog.entries[0])).catch(() => undefined);
}
